import React from 'react';
import { useIntl } from 'react-intl';
import { matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Badge, cx } from '@bolteu/clay-react';

import { $tid } from '@provider-portal/common/utils/testIdConfig';

import { ConfigMenuItem } from 'root/navigation/navTypes';
import { MenuContext } from 'root/navigation/navStructureUtils';
import { useVisitedNewBadges } from 'root/navigation/useVisitedNewBadges';

import { $tk } from 'utils/appTranslationKey';

interface SideMenuItemProps {
  text: string;
  index: string;
  isSelected: boolean;
  disabled: boolean;
  href: string;
  badgeLabel?: string;
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
}

const navigationItemIdsToTestIdsMapping: Record<string, string> = {
  dashboard: $tid.components.SideBar.Analytics.DashBoard,
  merchantWeeklyReport: $tid.components.SideBar.Analytics.WeeklyReport,
  itemReport: $tid.components.SideBar.Analytics.DetailedReports,
  invoicesAndSalesReports: $tid.components.SideBar.Analytics.InvoicesAndSales,
  orderHistory: $tid.components.SideBar.Analytics.OrderHistory,
  'org-summary': $tid.components.SideBar.MenuEditor.OrgSummary,
  'my-menu': $tid.components.SideBar.MenuEditor.MyMenu,
  items: $tid.components.SideBar.MenuEditor.Items,
  modifiers: $tid.components.SideBar.MenuEditor.Modifiers,
  timetables: $tid.components.SideBar.MenuEditor.Timetables,
};

const getTestIdForItemId = (itemId: string): string => {
  if (navigationItemIdsToTestIdsMapping[itemId]) {
    return navigationItemIdsToTestIdsMapping[itemId];
  }

  return '';
};

export const NavMenuItem = ({
  text,
  index,
  isSelected,
  disabled,
  href,
  badgeLabel,
  onMenuItemClick,
}: SideMenuItemProps) => {
  const handleClick = () => {
    onMenuItemClick?.({
      id: index,
      titleIntlId: text,
      href,
      disabled,
    });
  };

  return (
    <li className="relative mb-2 flex w-full items-center">
      <NavLink
        to={href}
        aria-label={text}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        data-testid={getTestIdForItemId(index)}
        onClick={handleClick}
        className={cx(
          'relative box-border flex min-w-0 grow items-center justify-start gap-2 rounded-md p-3 text-left text-primary no-underline',
          'transition-[background-color] duration-150 ease-in-out hover:bg-neutral-secondary',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:[outline-color:var(--color-bg-action-primary)]',
          isSelected && 'bg-neutral-secondary',
          disabled && 'pointer-events-none opacity-38',
        )}
      >
        <span
          className={cx(
            'block min-w-0 text-primary',
            isSelected ? 'bolt-font-body-m-accent' : 'bolt-font-body-m-regular',
          )}
        >
          {text}
        </span>
        {badgeLabel && (
          <Badge type="pill" size="sm" variant="action-secondary">
            <Badge.Content>{badgeLabel}</Badge.Content>
          </Badge>
        )}
      </NavLink>
    </li>
  );
};

export interface NavBarMenuItem extends ConfigMenuItem {
  disabled?: boolean;
}
export interface NavBarMenuProps {
  menuItems: NavBarMenuItem[];
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
}

export function NavBarMenu({ menuItems, onMenuItemClick }: NavBarMenuProps) {
  const location = useLocation();
  const intl = useIntl();
  const menuContext = React.useContext(MenuContext);
  const visitedBadgeHrefs = useVisitedNewBadges({
    menuItems,
    pathname: location.pathname,
  });

  const menuItemsLayout = (() => {
    return menuItems.map((item) => {
      if (item.hidden) {
        return null;
      }
      const isPathMatch = matchPath(location.pathname, {
        path: item.href,
      });

      const shouldOverrideActiveState = menuContext?.activeMenuEntryIdOverride === item.id;
      const isSelected = isPathMatch || shouldOverrideActiveState;
      const shouldShowNewBadge =
        item.showNewBadge && !isPathMatch && !visitedBadgeHrefs.includes(item.href);

      return (
        <NavMenuItem
          key={item.id}
          text={intl.formatMessage({ id: item.titleIntlId })}
          index={item.id}
          isSelected={!!isSelected}
          disabled={item.disabled ?? false}
          href={item.href}
          badgeLabel={
            shouldShowNewBadge
              ? intl.formatMessage({ id: $tk.components.NavBar.newBadge })
              : undefined
          }
          onMenuItemClick={onMenuItemClick}
        />
      );
    });
  })();

  return <ul className="relative m-0 list-none py-2 px-0">{menuItemsLayout}</ul>;
}
